import { useState, useEffect, useRef, useContext, useMemo } from "react";
import TableSelectModal from "./TableSelectModal";
import DineScreen from "./DineScreen";
import usePosOrders from "../../../services/Pos/usePosOrders";
import MobileNumberModal from "./MobileNumberModal";
import ViewPosOrderModal from "./ViewPosSearchBillModal";
import PaymentModal from "./PaymentModel";
import { GlobalContext } from "../../../Context/GlobalContext";
import {
  AlertTriangle,
  LockKeyhole,
  RefreshCcw,
  RefreshCw,
  Wallet,
} from "lucide-react";
import LoadingScreen from "../../../utils/LoadingScreen";
import NoIndex from "../../../components/NoIndex";
import { toast } from "react-toastify";
import PosOrderDetailsModal from "./PosOrderDetailsModal";
import PosDrawerOperationModal from "./PosDrawerOperationModal";
import PosPinScreen from "../Pos/PosPinScreen";
import useDining from "../../../services/Order/useDining";
import {
  normalizeOrderType,
  today,
  isOrderPaidOrSettled,
  hasPreviousPayment,
  getDefaultDrawerState,
  getSessionUser,
  parseUserRole,
  getPosPinSessionKey,
  getDrawerStorageKey,
  normalizePosButtonValue,
  getUserDrawerIds,
  getCatalogStorageKey,
  normalizeTaxConfig,
  getOrgTaxStorageKey,
  getCartFromOrder,
} from "./PosOrderHelper";
import PosOrderTable from "./PosOrderTable";

// ── helpers

const PAY_FILTERS = [
  // { key: "all", label: "ALL" },
  { key: "open", label: "OPEN" },
  { key: "paid", label: "PAID" },
  { key: "completed", label: "COMPLETED" },
];

// ── sub-components

function FilterBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-4 md:px-6 md:py-5 lg:px-8 lg:py-6 mb-5 
        text-[11px] md:text-sm lg:text-base 
        rounded-lg font-semibold border transition-all duration-150 ${
          active
            ? "bg-emerald-500 text-white border-emerald-500"
            : "bg-white text-gray-600 border-gray-400 hover:border-emerald-400 hover:text-emerald-600"
        }`}
    >
      {label}
    </button>
  );
}

// ── main component ─

export default function PosOrderManagement() {
  const {
    loading,
    orders,
    getOrders,
    generateOrderID,
    updatePosOrder,
    getCatalog,
    getOrganizationTaxConfig,
    taxConfigLoading,
    assignDeliveryPartner,
    getOrderDetails,
    validatePosPin,
    drawerOperation,
    getDrawerCurrentBalance,

    orderOptions,
    orderOptionsLoading,
    getPosOrderOptions,

    posButtonsMaster,
    posButtonsMasterLoading,
    getPosButtonsMaster,
  } = usePosOrders();

  const { refreshDiningCache } = useDining();

  // pos pin helper function

  const handleValidatePosPin = async (pin) => {
    const currentUser = sessionUser || getSessionUser();
    const { fkuserid } = parseUserRole(currentUser?.userRole);

    if (!currentUser?.userid) {
      toast.error("User ID missing. Please login again.");
      return;
    }

    if (!fkuserid) {
      toast.error("FK User ID missing from user role.");
      return;
    }

    if (!pin || pin.length !== 6) {
      toast.error("Please enter a valid 6 digit PIN.");
      return;
    }

    const payload = {
      uscode: String(currentUser.userid),
      fk_userid: String(fkuserid),
      pin: String(pin),
    };

    try {
      setPosPinLoading(true);

      const res = await validatePosPin(payload);

      const authResult = res?.response?.[0];

      const isAuthenticated =
        res?.responseCode === "00" && authResult?.result === "Y";

      if (isAuthenticated) {
        const storageKey = getPosPinSessionKey(currentUser);

        sessionStorage.setItem(storageKey, "true");
        setPosPinVerified(true);

        toast.success(authResult?.message || "POS unlocked successfully");

        fetchDrawerCurrentBalance({ silent: true });

        return;
      }

      sessionStorage.setItem(getPosPinSessionKey(currentUser), "false");
      setPosPinVerified(false);

      toast.error(
        authResult?.message ||
          res?.responseMessage ||
          "Invalid POS PIN. Please try again.",
      );
    } catch (error) {
      console.error("POS PIN validation error:", error);

      sessionStorage.setItem(getPosPinSessionKey(currentUser), "false");
      setPosPinVerified(false);

      toast.error("Something went wrong while validating POS PIN");
    } finally {
      setPosPinLoading(false);
    }
  };

  const [payFilter, setPayFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [orderType, setOrderType] = useState("");
  const { userID } = useContext(GlobalContext);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [sessionUser] = useState(() => getSessionUser());

  const [posPinVerified, setPosPinVerified] = useState(() => {
    const currentUser = getSessionUser();
    const storageKey = getPosPinSessionKey(currentUser);

    return sessionStorage.getItem(storageKey) === "true";
  });

  const [posPinLoading, setPosPinLoading] = useState(false);

  // dine flow state
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showDineScreen, setShowDineScreen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [generatedOrderId, setGeneratedOrderId] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleOrder, setSettleOrder] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tabletMode, setTabletMode] = useState(false);

  const [drawerModalOpen, setDrawerModalOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerStatusLoading, setDrawerStatusLoading] = useState(false);
  const [drawerState, setDrawerState] = useState(() => getDefaultDrawerState());

  const [loadingMenu, setLoadingMenu] = useState(false);
  const [organizationTaxConfig, setOrganizationTaxConfig] = useState({
    gstEnabled: "N",
    gstType: "excl",
    totalTaxRate: 0,
    cgstRate: 0,
    sgstRate: 0,
  });

  const [organizationRawConfig, setOrganizationRawConfig] = useState({});

  const posRef = useRef(null);

  const fetchCatalogueByType = async (type) => {
    try {
      const data = await getCatalog({
        orderType: type,
        force: true,
      });

      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("Catalog fetch error", err);
      return [];
    }
  };

  const preloadCatalogueCache = async () => {
    try {
      setLoadingMenu(true);

      const storageKey = getCatalogStorageKey(userID);
      const cachedRaw = sessionStorage.getItem(storageKey);

      if (cachedRaw) {
        try {
          const parsed = JSON.parse(cachedRaw);

          if (
            parsed?.takeaway?.length &&
            parsed?.home_delivery?.length &&
            parsed?.dining?.length
          ) {
            return;
          }
        } catch {
          sessionStorage.removeItem(storageKey);
        }
      }

      const [takeaway, home_delivery, dining] = await Promise.all([
        fetchCatalogueByType("takeaway"),
        fetchCatalogueByType("home_delivery"),
        fetchCatalogueByType("dining"),
      ]);

      const preparedCache = {
        takeaway,
        home_delivery,
        dining,
      };

      sessionStorage.setItem(storageKey, JSON.stringify(preparedCache));
    } catch (err) {
      console.error("Menu load error", err);
    } finally {
      setLoadingMenu(false);
    }
  };

  const refreshCatalogueCache = async () => {
    try {
      setLoadingMenu(true);

      const storageKey = getCatalogStorageKey(userID);

      sessionStorage.removeItem(storageKey);

      const [takeaway, home_delivery, dining] = await Promise.all([
        fetchCatalogueByType("takeaway"),
        fetchCatalogueByType("home_delivery"),
        fetchCatalogueByType("dining"),
      ]);

      const preparedCache = { takeaway, home_delivery, dining };

      sessionStorage.setItem(storageKey, JSON.stringify(preparedCache));
      await refreshDiningCache();

      console.log("Menu, tables and rooms synced");
    } catch (err) {
      console.error(" Sync failed", err);
    } finally {
      setLoadingMenu(false);
    }
  };

  const handleSyncTaxConfig = async () => {
    try {
      const storageKey = getOrgTaxStorageKey(userID);
      const rawStorageKey = `${storageKey}_raw`;

      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(rawStorageKey);

      const raw = await getOrganizationTaxConfig();

      if (!raw || Object.keys(raw).length === 0) {
        toast.error("Tax config not found");
        return;
      }

      const normalized = normalizeTaxConfig(raw);

      sessionStorage.setItem(storageKey, JSON.stringify(normalized));
      sessionStorage.setItem(rawStorageKey, JSON.stringify(raw));

      setOrganizationTaxConfig(normalized);
      setOrganizationRawConfig(raw);
      toast.success("Tax config synced successfully");

      console.log("RAW TAX CONFIG:", raw);
      console.log("NORMALIZED TAX CONFIG:", normalized);
    } catch (err) {
      console.error("Tax sync error", err);
      toast.error("Tax config sync failed");
    }
  };

  const handleMarkAsDelivered = async (order) => {
    const finalOrder = Array.isArray(order) ? order[0] : order;

    const finalOrderId =
      finalOrder?.orderid || finalOrder?.orderId || finalOrder?.order_id || "";

    if (!finalOrderId) {
      toast.error("Order ID missing");
      return;
    }

    try {
      setMarkingComplete(true);

      const response = await updatePosOrder({
        orderId: finalOrderId,
      });

      console.log("UPDATE POS ORDER RESPONSE:", response);

      if (response?.responseCode !== "00") {
        toast.error(
          response?.responseMessage || "Unable to mark order as delivered",
        );
        return;
      }

      toast.success(response?.responseMessage || "Order marked as delivered");

      const t = today();

      await getOrders({
        fromDate: t,
        toDate: t,
      });

      window.dispatchEvent(new Event("order-updated"));

      setSelected(null);
      setViewOrder(null);
      setShowDetailsModal(false);
    } catch (error) {
      console.error("Mark as delivered error:", error);

      toast.error("Unable to mark order as delivered");
    } finally {
      setMarkingComplete(false);
    }
  };

  useEffect(() => {
    const loadTax = async () => {
      if (!userID) return;

      const storageKey = getOrgTaxStorageKey(userID);
      const rawStorageKey = `${storageKey}_raw`;

      const cached = sessionStorage.getItem(storageKey);
      const rawCached = sessionStorage.getItem(rawStorageKey);

      try {
        if (cached) {
          const parsed = JSON.parse(cached);
          setOrganizationTaxConfig(parsed);
        }

        if (rawCached) {
          const parsedRaw = JSON.parse(rawCached);
          setOrganizationRawConfig(parsedRaw);
          return;
        }
      } catch (error) {
        console.error("Tax cache parse error:", error);

        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(rawStorageKey);
      }

      const raw = await getOrganizationTaxConfig();
      const normalized = normalizeTaxConfig(raw);

      sessionStorage.setItem(storageKey, JSON.stringify(normalized));
      sessionStorage.setItem(rawStorageKey, JSON.stringify(raw));

      setOrganizationTaxConfig(normalized);
      setOrganizationRawConfig(raw);
    };

    loadTax();
  }, [userID]);

  useEffect(() => {
    if (!userID) return;

    preloadCatalogueCache();
  }, [userID]);

  const hasFetchedPosButtonsRef = useRef(false);

  useEffect(() => {
    if (!userID || hasFetchedPosButtonsRef.current) return;

    hasFetchedPosButtonsRef.current = true;

    const fetchPosButtonsData = async () => {
      try {
        const [masterResponse, userOptionsResponse] = await Promise.all([
          getPosButtonsMaster(),
          getPosOrderOptions(),
        ]);

        console.log("POS BUTTONS MASTER:", masterResponse);
        console.log("USER POS ORDER OPTIONS:", userOptionsResponse);
      } catch (error) {
        console.error("POS buttons fetch error:", error);
      }
    };

    fetchPosButtonsData();
  }, [userID]);

  const hasFetchedOrdersRef = useRef(false);

  useEffect(() => {
    const fetchLatestOrders = () => {
      const t = today();
      getOrders({ fromDate: t, toDate: t });
    };

    const handleUpdate = () => {
      fetchLatestOrders();
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        fetchLatestOrders();
      }
    };

    // Initial load only once
    if (!hasFetchedOrdersRef.current) {
      hasFetchedOrdersRef.current = true;
      fetchLatestOrders();
    }

    // Refresh when order actually updates
    window.addEventListener("order-updated", handleUpdate);
    window.addEventListener("orderUpdated", handleUpdate);

    // Refresh when user returns to tab/window
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      window.removeEventListener("order-updated", handleUpdate);
      window.removeEventListener("orderUpdated", handleUpdate);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, []);

  useEffect(() => {
    if (!posPinVerified) return;

    fetchDrawerCurrentBalance({ silent: true });
  }, [posPinVerified]);

  const safeOrders = Array.isArray(orders) ? orders : [];

  const filtered = safeOrders.filter((o) => {
    const status = String(o.status || "")
      .toLowerCase()
      .trim();
    const paymentStatus = String(o.payment_status || "")
      .toLowerCase()
      .trim();
    const deliveryStatus = String(o.delivery_partner_status || "")
      .toLowerCase()
      .trim();

    const normalizedOrderType = normalizeOrderType(o.orderType);
    const normalizedTypeFilter = normalizeOrderType(typeFilter);

    let payMatch = true;

    if (payFilter === "open") {
      if (normalizedOrderType === "home_delivery") {
        payMatch = deliveryStatus !== "successful";
      } else {
        payMatch = !["completed", "closed", "delivered"].includes(status);
      }
    }

    if (payFilter === "paid") {
      payMatch = ["paid", "partially_paid"].includes(paymentStatus);
    }

    if (payFilter === "completed") {
      if (normalizedOrderType === "home_delivery") {
        payMatch =
          deliveryStatus === "successful" ||
          ["completed", "closed", "delivered"].includes(status);
      } else {
        payMatch = ["completed", "closed", "delivered"].includes(status);
      }
    }

    const typeMatch =
      typeFilter === "all" || normalizedOrderType === normalizedTypeFilter;

    return payMatch && typeMatch;
  });

  const selectedOrder = selected !== null ? filtered[selected] : null;

  const visibleOrderOptions = useMemo(() => {
    const masterButtons = Array.isArray(posButtonsMaster)
      ? posButtonsMaster
      : [];

    const userButtons = Array.isArray(orderOptions) ? orderOptions : [];

    const userOptionsMap = new Map(
      userButtons.map((userButton) => {
        const normalizedValue = normalizePosButtonValue(userButton?.value);

        return [normalizedValue, userButton];
      }),
    );

    return masterButtons
      .map((masterButton) => {
        const normalizedType = normalizePosButtonValue(masterButton?.value);

        const userButton = userOptionsMap.get(normalizedType);

        const masterStatus = String(masterButton?.status || "N")
          .trim()
          .toUpperCase();

        const userStatus = String(userButton?.status || "N")
          .trim()
          .toUpperCase();

        return {
          type: normalizedType,
          label:
            normalizedType === "dinein"
              ? "DINE-IN"
              : normalizedType === "homedelivery"
                ? "HOME\nDELIVERY"
                : normalizedType === "room_order"
                  ? "ROOM\nORDER"
                  : normalizedType === "takeaway"
                    ? "TAKEAWAY"
                    : masterButton?.label || userButton?.label || "ORDER",
          masterStatus,
          userStatus,
        };
      })
      .filter(
        (button) => button.masterStatus === "Y" && button.userStatus === "Y",
      )
      .filter((button) =>
        ["dinein", "takeaway", "homedelivery", "room_order"].includes(
          button.type,
        ),
      );
  }, [posButtonsMaster, orderOptions]);

  const visibleTypeFilters = useMemo(() => {
    const dynamicFilters = visibleOrderOptions.map((option) => {
      const normalizedType = normalizeOrderType(option.type);

      return {
        key: normalizedType,
        label:
          normalizedType === "dining"
            ? "DINE-IN"
            : normalizedType === "home_delivery"
              ? "HOME DELIVERY"
              : normalizedType === "room_order"
                ? "ROOM ORDER"
                : "TAKEAWAY",
      };
    });

    return [{ key: "all", label: "ALL" }, ...dynamicFilters];
  }, [visibleOrderOptions]);

  const canEditSelectedOrder =
    selectedOrder !== null && !isOrderPaidOrSettled(selectedOrder);

  const canSettleSelectedOrder =
    selectedOrder !== null && !isOrderPaidOrSettled(selectedOrder);

  const handleMobileSubmit = async (mobile) => {
    if (orderType === "dinein") {
      setGeneratedOrderId("");
      setMobile("");
      setShowMobileModal(false);
      setShowTableModal(true);
      return;
    }

    const id = await generateOrderID({
      mobile: mobile,
      orderType: orderType,
    });

    if (id) {
      setGeneratedOrderId(id);
      setMobile(mobile);
      setShowMobileModal(false);
      setShowDineScreen(true);
    }
  };

  const enterFullscreen = () => {
    const el = posRef.current || document.documentElement;

    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (selectedOrder?.customer_mobile) {
      setMobile(selectedOrder.customer_mobile);
    }
  }, [selectedOrder]);

  useEffect(() => {
    if (editOrder) {
      setShowDineScreen(true);
    }
  }, [editOrder]);

  useEffect(() => {
    if (typeFilter === "all") return;

    const isFilterAvailable = visibleTypeFilters.some(
      (filter) => filter.key === typeFilter,
    );

    if (!isFilterAvailable) {
      setTypeFilter("all");
      setSelected(null);
    }
  }, [visibleTypeFilters, typeFilter]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const saveDrawerState = (nextState) => {
    const storageKey = getDrawerStorageKey(sessionUser);

    const preparedState = {
      ...getDefaultDrawerState(),
      ...nextState,
    };

    localStorage.setItem(storageKey, JSON.stringify(preparedState));
    setDrawerState(preparedState);
  };

  const fetchDrawerCurrentBalance = async ({ silent = false } = {}) => {
    const currentUser = sessionUser || getSessionUser();
    const { fkuserid } = parseUserRole(currentUser?.userRole);

    if (!currentUser?.userid || !fkuserid) {
      if (!silent) toast.error("Drawer user details missing");
      return getDefaultDrawerState();
    }

    const { terminalId, drawerId } = getUserDrawerIds(currentUser);

    if (!terminalId || !drawerId) {
      if (!silent) {
        toast.error("Terminal ID or Drawer ID missing. Please login again.");
      }

      return getDefaultDrawerState();
    }

    const payload = {
      uscode: String(currentUser.userid),
      userid: String(fkuserid),
      terminalId,
      drawerId,
    };

    try {
      setDrawerStatusLoading(true);

      const res = await getDrawerCurrentBalance(payload);
      const resultData = Array.isArray(res?.response) ? res.response[0] : null;

      const isFound = res?.responseCode === "00" && resultData?.result === "Y";

      if (!isFound) {
        const closedState = {
          ...getDefaultDrawerState(),
          isOpen: false,
          drawerStatus: "CLOSED",
          currentBalance: "0",
          openingCash: "0",
          cashSales: "0",
          cardSales: "0",
          upiSales: "0",
          cashIn: "0",
          cashOut: "0",
          refund: "0",
        };

        saveDrawerState(closedState);

        if (!silent) {
          toast.info(resultData?.message || "No open drawer session found");
        }

        return closedState;
      }

      const isOpen =
        String(resultData?.drawerStatus || "").toUpperCase() === "OPEN";

      const nextState = {
        isOpen,
        drawerStatus: resultData?.drawerStatus || "CLOSE",
        sessionId: resultData?.sessionId || "",
        sessionCode: resultData?.sessionCode || "",

        openingCash: resultData?.openingCash || "",
        currentBalance: resultData?.currentBalance || "",
        cashSales: resultData?.cashSales || "",
        cardSales: resultData?.cardSales || "",
        upiSales: resultData?.upiSales || "",
        cashIn: resultData?.cashIn || "",
        cashOut: resultData?.cashOut || "",
        refund: resultData?.refund || "",

        openedAmount: resultData?.openingCash || "",
        expectedCash: resultData?.currentBalance || "",
        actualCash: resultData?.currentBalance || "",
      };

      saveDrawerState(nextState);
      return nextState;
    } catch (error) {
      console.error("fetchDrawerCurrentBalance error:", error);

      if (!silent) {
        toast.error("Unable to fetch drawer current status");
      }

      return getDefaultDrawerState();
    } finally {
      setDrawerStatusLoading(false);
    }
  };

  const isDrawerOpen =
    String(drawerState?.drawerStatus || "").toUpperCase() === "OPEN";

  const showDrawerClosedToast = () => {
    toast.error("Drawer is closed. Please open drawer before continuing.");
  };

  const canUsePos = () => {
    if (!isDrawerOpen) {
      showDrawerClosedToast();
      return false;
    }

    return true;
  };

  // pos lock helper function
  const handleLockPos = () => {
    const currentUser = sessionUser || getSessionUser();
    const storageKey = getPosPinSessionKey(currentUser);

    sessionStorage.setItem(storageKey, "false");
    setPosPinVerified(false);
    setSelected(null);

    toast.success("POS locked successfully");
  };

  const handleDrawerOperation = async ({
    amount,
    pin,
    note,
    status,
    terminalId,
    drawerid,
  }) => {
    const currentUser = sessionUser || getSessionUser();
    const { fkuserid } = parseUserRole(currentUser?.userRole);

    if (!currentUser?.userid) {
      toast.error("US code missing. Please login again.");
      return;
    }

    if (!fkuserid) {
      toast.error("User ID missing from user role.");
      return;
    }

    if (!pin || pin.length !== 6) {
      toast.error("Please enter valid 6 digit drawer PIN.");
      return;
    }

    if (!amount) {
      toast.error("Please enter drawer amount.");
      return;
    }

    const userDrawerIds = getUserDrawerIds(currentUser);

    const finalTerminalId = String(
      terminalId || userDrawerIds.terminalId || "",
    ).trim();
    const finalDrawerId = String(
      drawerid || userDrawerIds.drawerId || "",
    ).trim();

    if (!finalTerminalId || !finalDrawerId) {
      toast.error("Terminal ID or Drawer ID missing. Please login again.");
      return;
    }

    const payload = {
      uscode: String(currentUser.userid),
      userid: String(fkuserid),
      terminalId: finalTerminalId,
      drawerid: finalDrawerId,
      pin: String(pin),
      status: String(status),
      amount: String(amount),
      note: note || "",
    };

    try {
      setDrawerLoading(true);

      const res = await drawerOperation(payload);
      const resultData = Array.isArray(res?.response) ? res.response[0] : null;

      const isSuccess =
        res?.responseCode === "00" && resultData?.result === "Y";

      if (!isSuccess) {
        toast.error(
          resultData?.message ||
            res?.responseMessage ||
            "Drawer operation failed",
        );

        await fetchDrawerCurrentBalance({ silent: true });
        return;
      }

      toast.success(resultData?.message || "Drawer operation successful");

      setDrawerModalOpen(false);

      await fetchDrawerCurrentBalance({ silent: true });
    } catch (error) {
      console.error("handleDrawerOperation error:", error);
      toast.error("Something went wrong while processing drawer");
    } finally {
      setDrawerLoading(false);
    }
  };

  function DrawerBalanceCard({ drawerState, drawerStatusLoading }) {
    const formatMoney = (value) => {
      const amount = Number(value || 0);

      return amount.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      });
    };

    const drawerStatus = String(
      drawerState?.drawerStatus || "CLOSED",
    ).toUpperCase();

    const isOpen = drawerStatus === "OPEN";

    return (
      <>
 
      <div
        className={`h-[32px] rounded-lg border px-2 flex items-center gap-1.5 shadow-sm transition-all ${
          isOpen
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : "bg-red-50 border-red-200 text-red-700"
        }`}
      >
        <div className="flex items-center gap-1">
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              drawerStatusLoading
                ? "bg-amber-400 animate-pulse"
                : isOpen
                  ? "bg-emerald-500"
                  : "bg-red-500"
            }`}
          />

          <span className="text-[9px] font-extrabold uppercase tracking-wide">
            Drawer
          </span>
        </div>

        <span
          className={`text-[8px] font-black px-1.5 py-[1px] rounded-full ${
            drawerStatusLoading
              ? "bg-amber-100 text-amber-700"
              : isOpen
                ? "bg-emerald-100 text-emerald-700"
                : "bg-red-100 text-red-700"
          }`}
        >
          {drawerStatusLoading ? "CHECKING" : drawerStatus}
        </span>

        <div className="h-3.5 w-px bg-current/20" />

        <span className="text-[11px] font-black text-gray-900 whitespace-nowrap">
          {formatMoney(drawerState?.currentBalance)}
        </span>
      </div>
      </>
    );
  }

  if (!posPinVerified) {
    return (
      <>
        <NoIndex />

        <PosPinScreen
          sessionUser={sessionUser}
          loading={posPinLoading}
          onValidate={handleValidatePosPin}
        />
      </>
    );
  }

const receiptRawData = Array.isArray(organizationRawConfig?.response)
  ? organizationRawConfig.response[0] || {}
  : Array.isArray(organizationRawConfig)
    ? organizationRawConfig[0] || {}
    : organizationRawConfig?.response &&
        typeof organizationRawConfig.response === "object"
      ? organizationRawConfig.response
      : organizationRawConfig || {};

const reciptDetails = {
  userid: userID,
  business_name:
    receiptRawData?.business_name ||
    receiptRawData?.businessName ||
    receiptRawData?.organization_name ||
    receiptRawData?.restaurant_name ||
    receiptRawData?.shop_name ||
    receiptRawData?.name ||
    organizationTaxConfig?.organizationName ||
    "",

  address:
    receiptRawData?.address ||
    receiptRawData?.organization_address ||
    receiptRawData?.restaurant_address ||
    receiptRawData?.business_address ||
    receiptRawData?.shop_address ||
    receiptRawData?.full_address ||
    receiptRawData?.store_address ||
    receiptRawData?.branch_address ||
    receiptRawData?.billing_address ||
    "",

  phone_no:
    receiptRawData?.phone_no ||
    receiptRawData?.phoneNo ||
    receiptRawData?.phone ||
    receiptRawData?.mobile ||
    receiptRawData?.mobile_no ||
    receiptRawData?.mobileNo ||
    receiptRawData?.restaurant_mobile ||
    receiptRawData?.organization_mobile ||
    receiptRawData?.shop_mobile ||
    receiptRawData?.contact_number ||
    receiptRawData?.contactNo ||
    receiptRawData?.contact_no ||
    receiptRawData?.business_mobile ||
    "",

  gst:
    receiptRawData?.gst ||
    receiptRawData?.gst_no ||
    receiptRawData?.gstNo ||
    receiptRawData?.gstNumber ||
    receiptRawData?.gst_number ||
    organizationTaxConfig?.gstNumber ||
    "",

  fssaiNo:
    receiptRawData?.fssaiNo ||
    receiptRawData?.fssai_no ||
    receiptRawData?.fssai ||
    receiptRawData?.fssai_number ||
    "",

  taxCgst: organizationTaxConfig?.cgstRate || 0,
  taxSgst: organizationTaxConfig?.sgstRate || 0,

  tag_line1:
    receiptRawData?.tag_line1 || receiptRawData?.tagLine1 || "Visit Again",
};

  if (showSettleModal && settleOrder) {
    return (
      <PaymentModal
        cart={getCartFromOrder(settleOrder)}
        total={parseFloat(settleOrder.totalOrderPrice || 0)}
        orderId={settleOrder.orderid}
        customerMobile={settleOrder.customer_mobile}
        orderType={
          settleOrder.orderType === "dinein" ? "dining" : settleOrder.orderType
        }
        isPosTableSettlement={hasPreviousPayment(settleOrder)}
        reciptDetails={reciptDetails}
        selectedTable={{
          tableNo: settleOrder.table_no,
        }}
        isFullscreen={isFullscreen}
        enterFullscreen={enterFullscreen}
        exitFullscreen={exitFullscreen}
        tabletMode={tabletMode}
        setTabletMode={setTabletMode}
        setSelected={setSelected}
        refreshCatalogueCache={refreshCatalogueCache}
        loadingMenu={loadingMenu}
        handleSyncTaxConfig={handleSyncTaxConfig}
        taxConfigLoading={taxConfigLoading}
        onClose={async () => {
          setShowSettleModal(false);
          setSettleOrder(null);

          const t = today();

          await getOrders({
            fromDate: t,
            toDate: t,
          });

          setSelectedTable(null);
          setEditOrder(null);
          setSelected(null);
          setMobile("");
          setGeneratedOrderId("");
          setOrderType("");
          setShowMobileModal(false);
        }}
      />
    );
  }

  if (showDineScreen) {
    return (
      <DineScreen
        table={selectedTable}
        orderType={orderType}
        orderId={generatedOrderId}
        mobile={mobile}
        existingItems={editOrder?.items || []}
        reciptDetails={reciptDetails}
        onHoldSuccess={async () => {
          const t = today();

          await getOrders({
            fromDate: t,
            toDate: t,
          });

          if (window.getResturantTable) {
            await window.getResturantTable();
          }

          setShowDineScreen(false);
          setSelectedTable(null);
          setSelected(null);
        }}
        onBack={() => {
          setShowDineScreen(false);
          setShowTableModal(true);
        }}
        onClose={() => {
          setShowDineScreen(false);
          setSelectedTable(null);
        }}
      />
    );
  }

  return (
    <>
      <NoIndex />

      <div
        ref={posRef}
        className={`flex flex-col lg:flex-row h-full overflow-hidden montserrat-font-1 ${
          isFullscreen ? "bg-white" : ""
        } ${tabletMode ? "p-2 gap-2 text-xs" : "p-3 gap-3 text-sm"}`}
      >
        {/* ── MAIN PANEL ── */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 montserrat-font-1">
          {/* FILTER BAR */}

          <div className="flex flex-col xl:flex-row gap-6 xl:items-start pb-2">
            {/* LEFT - Payment Status */}
            <div className="flex flex-col gap-2 min-w-fit">
              <span className="h-5 text-[12px] font-mono font-semibold tracking-widest text-gray-800 uppercase">
                Filter by Payment Status
              </span>

              <div className="flex flex-wrap gap-3 items-center">
                {PAY_FILTERS.map((f) => (
                  <FilterBtn
                    key={f.key}
                    label={f.label}
                    active={payFilter === f.key}
                    onClick={() => {
                      setPayFilter(f.key);
                      setSelected(null);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden xl:block w-px h-16 bg-gray-300 mt-5" />

            {/* RIGHT - Order Type */}
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="h-5 text-[12px] font-mono font-semibold tracking-widest text-gray-800 uppercase">
                  Filter by Order Type
                </span>
              </div>

              <div className="flex flex-wrap gap-3 items-center">
                {visibleTypeFilters.map((f) => (
                  <FilterBtn
                    key={f.key}
                    label={f.label}
                    active={typeFilter === f.key}
                    onClick={() => {
                      setTypeFilter(f.key);
                      setSelected(null);
                    }}
                  />
                ))}

                <DrawerBalanceCard
                  drawerState={drawerState}
                  drawerStatusLoading={drawerStatusLoading}
                />
              </div>

              {/* Mobile Refresh */}
              <button
                onClick={() => {
                  if (!canUsePos()) return;

                  const t = today();
                  getOrders({ fromDate: t, toDate: t });
                }}
                className="flex md:hidden mt-2 items-center justify-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-semibold w-full"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-6">
              <LoadingScreen text="Updating....." />
            </div>
          )}

          {/* TABLE */}
          <PosOrderTable
            loading={loading}
            filteredOrders={filtered}
            selected={selected}
            setSelected={setSelected}
            tabletMode={tabletMode}
            canUsePos={canUsePos}
            onAssignDelivery={async (orderId) => {
              if (!canUsePos()) return;

              await assignDeliveryPartner({
                orderId,
              });

              const currentDate = today();

              await getOrders({
                fromDate: currentDate,
                toDate: currentDate,
              });
            }}
          />

          {/* ACTION BUTTONS */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-shrink-0 mt-3 montserrat-font-1">
            {[
              { label: "VIEW", enabled: true },
              { label: "EDIT", enabled: canEditSelectedOrder },
              { label: "SETTLE", enabled: canSettleSelectedOrder },
            ].map(({ label, enabled }) => (
              <button
                key={label}
                disabled={!enabled}
                onClick={() => {
                  if (!canUsePos()) return;

                  if (label === "VIEW") {
                    if (selectedOrder) {
                      setViewOrder(selectedOrder);
                      setShowDetailsModal(true);
                      setShowViewModal(false);
                    } else {
                      setViewOrder(null);
                      setShowDetailsModal(false);
                      setShowViewModal(true);
                    }

                    return;
                  }

                  if (label === "EDIT" && selectedOrder) {
                    if (isOrderPaidOrSettled(selectedOrder)) {
                      toast.error("Paid or settled orders cannot be edited");
                      return;
                    }

                    setEditOrder(selectedOrder);
                    setOrderType(selectedOrder.orderType);
                    setGeneratedOrderId(selectedOrder.orderid);
                    setMobile(selectedOrder.customer_mobile);
                    setSelectedTable(selectedOrder.table_no || null);
                  }

                  if (label === "SETTLE" && selectedOrder) {
                    if (isOrderPaidOrSettled(selectedOrder)) {
                      toast.error("This order is already paid or settled");
                      return;
                    }

                    setSettleOrder(selectedOrder);
                    setShowSettleModal(true);
                  }
                }}
                className={`rounded-2xl py-7 text-base font-bold tracking-wider border-2 border-gray-600 transition-all shadow-sm ${
                  enabled
                    ? "border-gray-200 text-gray-700 bg-white hover:border-emerald-400 hover:text-emerald-600 cursor-pointer"
                    : "border-gray-100 text-gray-300 bg-white cursor-not-allowed"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* BOTTOM TOOLBAR */}
          <div className="flex gap-3 flex-wrap items-center flex-shrink-0 mt-3 overflow-x-auto pb-2">
            <button
              onClick={() => {
                const t = today();
                getOrders({ fromDate: t, toDate: t });
              }}
              disabled={taxConfigLoading}
              className="bg-green-500 border border-green-600 text-white rounded-xl px-5 py-4 text-sm font-semibold transition-all shadow-sm flex items-center gap-1.5"
            >
              <RefreshCcw className="w-4 h-4" />
              {loading ? "Refreshing..." : "Refresh Orders"}
            </button>
            <button
              onClick={() => {
                if (isFullscreen) {
                  exitFullscreen();
                } else {
                  enterFullscreen();
                }
              }}
              className="bg-gray-500 border border-gray-600 text-white rounded-xl px-5 py-4 text-sm font-semibold transition-all shadow-sm"
            >
              {isFullscreen ? "Exit Full Screen" : "Full Screen"}
            </button>

            <button
              onClick={() => {
                setTabletMode((prev) => {
                  const newMode = !prev;
                  setSelected(null);
                  return newMode;
                });
              }}
              className="bg-gray-500 text-white border border-gray-500 rounded-xl px-5 py-4 text-sm font-semibold transition-all shadow-sm"
            >
              {tabletMode ? "Exit Tablet Mode" : "On Tablet Mode"}
            </button>
            <button
              onClick={refreshCatalogueCache}
              disabled={loadingMenu}
              className="bg-blue-500 border border-blue-600 text-white rounded-xl px-5 py-4 text-sm font-semibold transition-all shadow-sm flex items-center gap-1.5"
            >
              <RefreshCcw className="w-4 h-4" />
              {loadingMenu ? "Syncing..." : "Sync Menu"}
            </button>
            <button
              onClick={handleSyncTaxConfig}
              disabled={taxConfigLoading}
              className="bg-yellow-700 border border-yellow-600 text-white rounded-xl px-5 py-4 text-sm font-semibold transition-all shadow-sm flex items-center gap-1.5"
            >
              <AlertTriangle className="w-4 h-4" />
              {taxConfigLoading ? "Syncing..." : "Sync Tax Config"}
            </button>

            <button
              onClick={handleLockPos}
              className="bg-white border border-red-600 text-red-600 rounded-xl px-5 py-4 text-sm font-semibold hover:bg-red-50 transition-all shadow-sm flex items-center gap-1.5"
            >
              <LockKeyhole className="w-4 h-4" />
              Lock POS
            </button>

            <button
              onClick={() => setDrawerModalOpen(true)}
              disabled={drawerStatusLoading}
              className={`border rounded-xl px-5 py-4 text-sm font-semibold transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-60 ${
                drawerState?.isOpen
                  ? "bg-red-50 border-red-600 text-red-600 hover:bg-red-100"
                  : "bg-emerald-50 border-emerald-600 text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <Wallet className="w-4 h-4" />
              {drawerStatusLoading
                ? "Checking Drawer..."
                : drawerState?.isOpen
                  ? "Close Drawer"
                  : "Open Drawer"}
            </button>

          </div>
        </div>

        {/* ── RIGHT SIDEBAR ── */}

        <div
          className={`flex flex-row lg:flex-col flex-shrink-0 gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 ${
            tabletMode ? "lg:w-60 xl:w-70" : "lg:w-60 xl:w-70"
          }`}
        >
          {posButtonsMasterLoading || orderOptionsLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="min-w-[140px] lg:min-w-0 h-20 lg:h-20 flex-1 rounded-xl border-2 border-gray-200 bg-gray-100 animate-pulse"
              />
            ))
          ) : visibleOrderOptions.length === 0 ? (
            <div className="w-full min-h-20 flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-center text-sm text-gray-500">
              No POS order options enabled
            </div>
          ) : (
            visibleOrderOptions.map(({ label, type }) => {
              const count = safeOrders.filter(
                (order) =>
                  normalizeOrderType(order.orderType) ===
                  normalizeOrderType(type),
              ).length;

              return (
                <div
                  key={type}
                  onClick={() => {
                    if (!canUsePos()) return;

                    setOrderType(type);
                    setSelected(null);
                    setEditOrder(null);
                    setSelectedTable(null);

                    if (type === "dinein" || type === "room_order") {
                      setMobile("");
                      setGeneratedOrderId("");
                      setShowMobileModal(false);
                      setShowTableModal(true);
                    } else {
                      setGeneratedOrderId("");
                      setShowTableModal(false);
                      setShowMobileModal(true);
                    }
                  }}
                  className="min-w-[140px] lg:min-w-0 h-20 lg:h-20 flex-1 flex flex-col items-center justify-center rounded-xl border-2 border-gray-800 cursor-pointer transition-all group bg-white hover:bg-emerald-50 hover:border-emerald-500"
                >
                  <span className="text-xl lg:text-2xl font-bold tracking-widest uppercase text-center leading-snug whitespace-pre-line text-gray-900">
                    {label}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* ── DINE MODALS ── */}
        <TableSelectModal
          open={showTableModal}
          onClose={() => setShowTableModal(false)}
          onSelect={async (table) => {
            setSelectedTable(table);
            setShowTableModal(false);

            const isRoomOrder = normalizeOrderType(orderType) === "room_order";

            const currentOrderTypeForMatch = isRoomOrder
              ? "room_order"
              : "dining";

            const selectedNumber = String(
              table.tableNo ||
                table.table_no ||
                table.roomNumber ||
                table.room_no ||
                table.roomNo ||
                "",
            ).trim();

            if (table.mode === "reserved") {
              const matchedOrder = safeOrders.find((order) => {
                const orderTableNo = String(
                  order.table_no || order.tableNo || order.roomNumber || "",
                ).trim();

                return (
                  orderTableNo === selectedNumber &&
                  normalizeOrderType(order.orderType) ===
                    currentOrderTypeForMatch &&
                  order.status !== "completed"
                );
              });

              const reservedOrderId =
                table.orderId ||
                table.orderid ||
                table.posOrderId ||
                table.billNo ||
                matchedOrder?.orderid ||
                "";

              if (!reservedOrderId) {
                toast.error(
                  isRoomOrder
                    ? "Order ID not found for this reserved room"
                    : "Order ID not found for this reserved table",
                );

                console.log("Reserved selected data:", table);
                console.log("All orders:", safeOrders);
                return;
              }

              const details = await getOrderDetails(reservedOrderId);
              const fullOrder = Array.isArray(details) ? details[0] : details;

              setGeneratedOrderId(reservedOrderId);

              setMobile(
                table.mobile ||
                  table.customer_mobile ||
                  table.custMobile ||
                  matchedOrder?.customer_mobile ||
                  fullOrder?.customer_mobile ||
                  "",
              );

              setEditOrder(fullOrder || matchedOrder || table);
              setShowDineScreen(true);
              return;
            }

            const defaultMobile = table.mobile || mobile || "9999999999";

            const id = await generateOrderID({
              mobile: defaultMobile,
              orderType: isRoomOrder ? "room_order" : "dinein",
            });

            if (!id) {
              toast.error(
                isRoomOrder
                  ? "Room order ID generation failed"
                  : "Dine-in order ID generation failed",
              );
              return;
            }

            setGeneratedOrderId(id);
            setMobile(defaultMobile);
            setEditOrder(null);
            setShowDineScreen(true);
          }}
          mobile={mobile}
          orderType={orderType}
        />

        <MobileNumberModal
          open={showMobileModal}
          onClose={() => setShowMobileModal(false)}
          orderType={orderType}
          onSubmit={handleMobileSubmit}
        />

        <ViewPosOrderModal
          open={showViewModal}
          order={viewOrder}
          onClose={() => {
            setShowViewModal(false);
            setViewOrder(null);
          }}
          onEdit={(order) => {
            setEditOrder(order);
            setOrderType(order.orderType);
            setGeneratedOrderId(order.orderid);
            setMobile(order.customer_mobile);
            setSelectedTable(order.table_no || null);
            setShowViewModal(false);
            setViewOrder(null);
          }}
          onSettle={(order) => {
            setSettleOrder(order);
            setShowSettleModal(true);
            setShowViewModal(false);
            setViewOrder(null);
          }}
          onOrderCompleted={async () => {
            const t = today();

            await getOrders({
              fromDate: t,
              toDate: t,
            });

            await refreshDiningCache();

            setSelected(null);
            setViewOrder(null);
            setShowViewModal(false);
          }}
        />

        <PosOrderDetailsModal
          open={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setViewOrder(null);
          }}
          order={viewOrder}
          reciptDetails={reciptDetails}
          onEdit={(order) => {
            if (isOrderPaidOrSettled(order)) {
              toast.error("Paid or settled orders cannot be edited");
              return;
            }

            setShowDetailsModal(false);
            setEditOrder(order);
            setOrderType(order.orderType);
            setGeneratedOrderId(order.orderid);
            setMobile(order.customer_mobile);
            setSelectedTable(order.table_no || null);
          }}
          onSettle={(order) => {
            if (isOrderPaidOrSettled(order)) {
              toast.error("This order is already paid or settled");
              return;
            }

            setShowDetailsModal(false);
            setSettleOrder(order);
            setShowSettleModal(true);
          }}
          onMarkAsComplete={handleMarkAsDelivered}
          markingComplete={markingComplete}
        />

        <PosDrawerOperationModal
          show={drawerModalOpen}
          sessionUser={sessionUser}
          drawerState={drawerState}
          loading={drawerLoading}
          onClose={() => {
            if (drawerLoading) return;
            setDrawerModalOpen(false);
          }}
          onSubmit={handleDrawerOperation}
        />
      </div>
    </>
  );
}
